import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import cn from 'classnames';
import Collapse from '@mui/material/Collapse';

import { Meal as MealType, Meal, MealConsumer, MealCounter } from '../../types';
import { useTheme } from '../../../theme';
import { Money } from '../../../types';
import { Money as MoneyComponent } from '../../../Money';
import { ArrowIcon } from './Icons';
import Counter from '../../../Counter/Counter';
import { getPassengerAbbreviation } from '../../utils';
import { default as BaseButton } from '../../../BaseComponents/Button';
import SimpleLoader from '../../../SimpleLoader';
import { useToggleable } from '../../../hooks';
import { SafeHtml } from '@shared/ui';

interface Props {
	meal: Meal;
	counters: MealCounter[];
	onIncrease: (passengerId: string) => void;
	onDecrease: (passengerId: string) => void;
	onAddForAll: () => void;
	onClear: (meal: MealType) => void;
	onSubmit: () => void;
	oldPrice?: Money;
	readonly?: boolean;
	isLoading?: boolean;
	marker?: string;
	isDescriptionOpened?: boolean;
	isCountChanged?: boolean;
}

const Details: React.FC<Props> = ({
	marker,
	meal,
	oldPrice,
	counters,
	onDecrease,
	onIncrease,
	onClear,
	onSubmit,
	readonly,
	isLoading,
	isCountChanged,
	onAddForAll,
	isDescriptionOpened
}) => {
	const { t } = useTranslation('Meal');
	const { Details: css } = useTheme('Meal');

	const { isOpen: isOpenDescription, toggle: onToggleDescription } = useToggleable(isDescriptionOpened);

	const countersState = useMemo(() => {
		const totalPrice: Money = { amount: 0, currency: '' };

		let clearIsUnavailable = readonly || isLoading;
		let totalNumberOfMeals = 0;

		counters.forEach(counter => {
			if (counter.minAvailable > 0) {
				clearIsUnavailable = true;
			}

			totalNumberOfMeals += counter.count;
			totalPrice.amount = totalPrice.amount + counter.count * (meal.price?.amount ?? 0);
			totalPrice.currency = meal.price?.currency;
		});

		return { totalPrice, clearIsUnavailable, totalNumberOfMeals };
	}, [meal, counters, readonly, isLoading]);

	const canAddForAllPassengers = meal.allowedPassengers.length <= 4;

	const onDecreaseHandler = useCallback(
		(passenger: MealConsumer) => () => {
			if (isLoading) {
				return;
			}

			onDecrease(passenger.id);
		},
		[isLoading, onDecrease]
	);

	const onIncreaseHandler = useCallback(
		(passenger: MealConsumer) => () => {
			if (isLoading) {
				return;
			}

			onIncrease(passenger.id);
		},
		[isLoading, onIncrease]
	);

	return (
		<>
			{isLoading && <SimpleLoader isFullScreen={false} />}
			<div className={css.wrapper}>
				<div className={css.image} style={meal.imageURL ? { backgroundImage: `url(${meal.imageURL})` } : {}}>
					{marker && <div className={css.marker}>{marker}</div>}
				</div>

				<div className={css.content}>
					<div className={css.row}>
						<div className={css.name}>{meal.name}</div>

						<div className={css.priceWrapper}>
							{!!oldPrice?.amount && <MoneyComponent moneyClassName={css.oldPrice} money={oldPrice} />}

							{!!meal.price.amount ? (
								<MoneyComponent moneyClassName={css.price} money={meal.price} />
							) : (
								<span className={css.price}>{t('Free')}</span>
							)}
						</div>
					</div>

					<div className={css.row}>
						<span className={css.trigger} onClick={onToggleDescription}>
							{t('Show description')}

							<span
								className={cn(css.trigger__icon, {
									[css.trigger__icon_rotated]: isOpenDescription
								})}
							>
								{ArrowIcon}
							</span>
						</span>

						{!!meal.weight && (
							<span className={css.weight}>
								{meal.weight} {t('g')}
							</span>
						)}
					</div>

					<Collapse in={isOpenDescription} mountOnEnter={true} unmountOnExit={true}>
						<div className={css.description}>
							<SafeHtml tag="div" className={css.description__row} html={meal.description} />

							{!!meal.allergens?.length && (
								<div className={css.description__row}>
									<div className={css.description__header}>{t('Allergens')}</div>

									<div>{meal.allergens.join(', ')}</div>
								</div>
							)}
						</div>
					</Collapse>
				</div>

				<div className={css.selector}>
					<div className={css.counters}>
						{counters.map(({ passenger, count, minAvailable, maxAvailable }) => (
							<div
								key={passenger.id}
								className={css.counter}
								data-test-id="meal-counter"
								data-passenger-id={passenger.id}
							>
								<span className={css.passenger}>
									<span className={css.passenger__index}>
										{passenger.isIdentified
											? getPassengerAbbreviation(passenger.name)
											: parseInt(passenger.id) + 1}
									</span>

									<span>{passenger.name}</span>
								</span>

								<Counter
									classes={{
										minus: cn({ [css.disabled]: isLoading }),
										plus: cn({ [css.disabled]: isLoading })
									}}
									value={count}
									showOnlyIncreaseButtonWhenValueIsNull={true}
									minValue={minAvailable}
									maxValue={maxAvailable}
									onDecrease={onDecreaseHandler(passenger)}
									onIncrease={onIncreaseHandler(passenger)}
									readonly={readonly}
								/>
							</div>
						))}
					</div>

					<div className={css.footer}>
						{countersState.totalNumberOfMeals === 0 && canAddForAllPassengers && !readonly && (
							<span className={css.addForAll} data-test-id="meal-add-for-all" onClick={onAddForAll}>
								{t('Add for all')}
							</span>
						)}

						{countersState.totalNumberOfMeals > 0 && (
							<div className={css.footer__price}>
								{countersState.totalPrice.amount > 0 ? (
									<div className={css.totalPrice}>
										<span className={css.totalText}>{t('Total')}:&nbsp;</span>
										<MoneyComponent moneyClassName={css.total} money={countersState.totalPrice} />
									</div>
								) : (
									<span className={css.total}>{t('Free')}</span>
								)}

								{!countersState.clearIsUnavailable && (
									<div className={css.clear}>
										<span data-test-id="meal-clear" onClick={() => onClear(meal)}>
											{t('Clear')}
										</span>
									</div>
								)}
							</div>
						)}

						<BaseButton
							variant="smallPrimary"
							data-test-id="meal-confirm"
							className={cn(css.button, {
								[css.button_empty]: !isCountChanged && !readonly
							})}
							onClick={onSubmit}
						>
							{t('Done')}
						</BaseButton>
					</div>
				</div>
			</div>
		</>
	);
};

export default Details;
